import { useStoreLogger } from '@/logger.ts'
import type {
  IProductReviewResponse,
  IProductReviewCreate,
  IProductReviewDelete,
  IProductReviewFileCreate,
  IProductReviewFileResponse,
  IProductReviewUpdate
} from '@/types/ProductReviewTypes'
import {
  ProductReviewsGet,
  ProductReviewCreate,
  ProductReviewDelete,
  ProductReviewFileCreate,
  ProductReviewUpdate
} from '@/api/ProductReview.ts'
import { GetCacheItem, SetCacheItem, RemoveCacheItem } from '@/services/CacheService.ts'

export const ProductReviewLocalStorageKey = 'AoeProductReview_'

export function GetProductReviewsFromLocalStorage(
  productCode: string
): IProductReviewResponse | null {
  const logger = useStoreLogger()
  logger.debug('GetProductReviewsFromLocalStorage', productCode)
  const key = ProductReviewLocalStorageKey + productCode
  const cacheItem = GetCacheItem<string>(key)
  if (cacheItem) {
    logger.debug('Product Review found in local storage')
    const prodRev = JSON.parse(cacheItem) as IProductReviewResponse
    return prodRev
  }
  logger.debug('Product Review not found in local storage')
  return null
}

export async function GetProductReviewsFromLocalStorageOrApi(
  productCode: string
): Promise<IProductReviewResponse | null> {
  const logger = useStoreLogger()
  logger.debug('GetProductReviewsFromLocalStorageOrApi', productCode)

  let prodRev = GetProductReviewsFromLocalStorage(productCode)
  if (prodRev) {
    return prodRev
  }

  try {
    const revResponse = await ProductReviewsGet(productCode)

    if (revResponse) {
      SaveProductReviewsToLocalStorage(revResponse, productCode)
      prodRev = GetProductReviewsFromLocalStorage(productCode)
    }
    return revResponse
  } catch (error) {
    logger.error('GetProductReviewsFromLocalStorageOrApi - Error for', productCode, ':', error)
    return null
  }
}

export function SaveProductReviewsToLocalStorage(prodRevResponse: IProductReviewResponse, productCode: string) {
  const newValue = JSON.stringify(prodRevResponse)
  const logger = useStoreLogger()
  logger.debug('SaveProductReviewsToLocalStorage', newValue)
  const key = ProductReviewLocalStorageKey + productCode
  SetCacheItem(key, newValue, 5 * 60)
  window.dispatchEvent(
    new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
  )
}

export function ClearProductReviewCache(productCode: string) {
  const logger = useStoreLogger()
  logger.debug('ClearProductReviewCache', productCode)
  const key = ProductReviewLocalStorageKey + productCode
  RemoveCacheItem(key)
}

export async function ProductReviewAdd(productReviewCreate: IProductReviewCreate) : Promise<number> {
  const response = await ProductReviewCreate(productReviewCreate)

  if (!response || !response.aoeProductReviewId) {
    throw new Error('API returned invalid response - no aoeProductReviewId')
  }

  return response.aoeProductReviewId
}

export async function ProductReviewEdit(productReviewUpdate: IProductReviewUpdate) : Promise<number> {
  const response = await ProductReviewUpdate(productReviewUpdate)

  if (!response || !response.aoeProductReviewId) {
    throw new Error('API returned invalid response - no aoeProductReviewId')
  }

  return response.aoeProductReviewId
}

export async function ProductReviewRemove(productReviewDelete: IProductReviewDelete) {
  await ProductReviewDelete(productReviewDelete)
}

export async function ProductReviewFileAdd(productReviewFileCreate: IProductReviewFileCreate, formData : FormData) : Promise<IProductReviewFileResponse> {
  const resp: IProductReviewFileResponse = ({ error: null })
  await ProductReviewFileCreate(productReviewFileCreate, formData).then(response => {
    if (response.code == "ERR_NETWORK")
    {
      resp.error = response.code
    }

    if (resp.error != null)
    {
      ProductReviewRemove({ productCode: productReviewFileCreate.productCode, aoeProductReviewId: productReviewFileCreate.aoeProductReviewId })
    }
  })

  return resp;
}
